// @givmo/board — starting the board's self-assessment (CBG-YR4-ASSESS-WEB, frame s1).
//
// ══ IT SHOWS EVERYTHING THAT WILL HAPPEN BEFORE IT HAPPENS ══════════════════════════════════════
//
// The annual disclosures' start dialog in its own words: a name, the day answers are due, the one
// promise about privacy — said once, before the questions — Givmo's starting statements as the backend
// serves them (its name and provenance line, each statement's words and source, approved) with a
// switch each to leave one out this time, who's asked (everyone on the roster now, directors or not,
// D-CBG-21, unless the officer unticks someone), and the email each person gets. Nothing is sent
// until "Start and send". The instrument's own rule is said where a choice would break it: at least
// one statement to rate, at least one person asked (the backend's own sentences, approved).
//
// Nothing here says when a board "should" run one, or that one is required (plan §3.2, §3.6).

import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { isGivmoError, type OrgBoardMemberOut } from '@givmo/api-client';
import {
  Button,
  Checkbox,
  ErrorMessage,
  Skeleton,
  Switch,
  TextField,
  toast,
} from '@givmo/design-system';
import { FieldNote } from '../../components/field-note';
import { useBoardOperations } from '../../board-runtime';
import { todayAsDateInput } from '../../lib/display';
import { capacityLine } from '../roster/officer-roles';
import {
  extractBoardMembers,
  isCommitteeOnlySeat,
  seatConnection,
  sortSeats,
} from '../roster/roster-decode';
import { DialogFrame, EmailPreview, PartLabel, SourceLine } from '../conflicts/coi-parts';
import { assessmentEmailLines } from './assessment-decode';
import { useRefreshSelfAssessments, useStartingInstrument } from './assessment-queries';
import { assessmentWords as w } from './assessment-words';

const SECONDARY = 'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary';
const PRIVACY = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary';
const STATEMENT = 'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-strong';

/** A calendar date a number of days after another (`YYYY-MM-DD`, the reader's own calendar). */
function daysAfter(iso: string, days: number): string {
  const [year, month, day] = iso.split('-').map(Number);
  return todayAsDateInput(new Date(year ?? 1970, (month ?? 1) - 1, (day ?? 1) + days).getTime());
}

export function AssessmentStartDialog({
  orgRef,
  label: initialLabel,
  organization,
  onClose,
}: {
  orgRef: string;
  /** The name the next one carries (`nextLabel`): this year, or the next one free. */
  label: string;
  /** The organization's name, or null where it doesn't resolve (the mails' unnamed forms). */
  organization: string | null;
  onClose: () => void;
}) {
  const operations = useBoardOperations();
  const refresh = useRefreshSelfAssessments(orgRef);
  const instrumentQuery = useStartingInstrument(orgRef, true);
  const rosterQuery = useQuery({
    ...operations.officerListBoardMembersOptions({ path: { org_ref: orgRef } }),
    select: (body: unknown) => sortSeats(extractBoardMembers(body)),
  });

  const [label, setLabel] = useState(initialLabel);
  const [dueOn, setDueOn] = useState(() => daysAfter(todayAsDateInput(), 28));
  const [off, setOff] = useState<ReadonlySet<string>>(new Set());
  const [unticked, setUnticked] = useState<ReadonlySet<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const instrument = instrumentQuery.data ?? null;
  // The board's seats: someone who isn't on the board is never asked (CBG-YR6-OUTSIDE, edge 11).
  const people = useMemo<OrgBoardMemberOut[]>(
    () =>
      (rosterQuery.data ?? []).filter(
        (seat) => seat.status === 'active' && !isCommitteeOnlySeat(seat),
      ),
    [rosterQuery.data],
  );
  const asked = people.filter((seat) => !unticked.has(seat.id));
  const notJoined = asked
    .filter((seat) => seatConnection(seat) !== 'joined' && (seat.email_of_record ?? '').length > 0)
    .map((seat) => seat.display_name);
  const noEmail = asked
    .filter((seat) => (seat.email_of_record ?? '').length === 0)
    .map((seat) => seat.display_name);
  const mailed = asked.filter((seat) => (seat.email_of_record ?? '').length > 0);
  // The email as most people get it: someone who has joined Board, else the first person mailed.
  const firstPerson = mailed.find((seat) => seatConnection(seat) === 'joined') ?? mailed[0] ?? null;

  const ratingIds = (instrument?.groups ?? []).flatMap((group) =>
    group.statements.map((statement) => statement.statement_id),
  );
  const ratingsOn = ratingIds.filter((id) => !off.has(id)).length;

  const open = useMutation({
    mutationFn: async () => {
      if (instrument === null) throw new Error(w.couldNotSave);
      const { data } = await operations.officerOpenBoardSelfAssessment({
        path: { org_ref: orgRef },
        body: {
          label: label.trim(),
          due_on: dueOn,
          instrument_template_id: instrument.template_id,
          instrument_version: instrument.version,
          statements_off: [...off],
          not_asked: [...unticked].filter((id) => people.some((seat) => seat.id === id)),
        },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: async (data) => {
      toast.success(
        w.started(label.trim(), typeof data?.emails_sent === 'number' ? data.emails_sent : 0),
      );
      await refresh();
      onClose();
    },
    onError: (caught) =>
      setError(isGivmoError(caught) && caught.message.trim() ? caught.message : w.couldNotSave),
  });

  const ready =
    instrument !== null &&
    label.trim().length > 0 &&
    dueOn.length > 0 &&
    ratingsOn > 0 &&
    asked.length > 0;

  const toggle =
    (set: (update: (current: ReadonlySet<string>) => ReadonlySet<string>) => void) =>
    (id: string, on: boolean) =>
      set((current) => {
        const next = new Set(current);
        if (on) next.delete(id);
        else next.add(id);
        return next;
      });
  const askStatement = toggle(setOff);
  const askPerson = toggle(setUnticked);

  return (
    <DialogFrame
      title={w.start(label.trim() || '…')}
      testId="assessment-start-dialog"
      width={640}
      onClose={onClose}
      footer={
        <>
          <Button
            appearance="saas"
            tone="secondary"
            onClick={onClose}
            testId="assessment-start-cancel"
          >
            {w.cancel}
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            disabled={!ready}
            loading={open.isPending}
            onClick={() => {
              setError(null);
              open.mutate();
            }}
            testId="assessment-start-send"
          >
            {w.startAndSend(asked.length)}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-12 sm:grid-cols-2">
        <TextField
          label={w.nameField}
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          maxLength={40}
          testId="assessment-start-name"
        />
        <TextField
          label={w.dueField}
          type="date"
          value={dueOn}
          onChange={(event) => setDueOn(event.target.value)}
          testId="assessment-start-due"
        />
      </div>
      <p className={PRIVACY} data-testid="assessment-start-privacy">
        {w.privacyLine}
      </p>

      <section className="flex flex-col gap-10" data-testid="assessment-start-questions">
        <PartLabel>{w.questionsLabel}</PartLabel>
        {instrumentQuery.isPending ? (
          <Skeleton className="h-84 w-full rounded-md" />
        ) : instrument === null ? (
          <ErrorMessage tone="muted">{w.questionsUnavailable}</ErrorMessage>
        ) : (
          <>
            <span className="flex flex-col gap-2">
              <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                {instrument.name}
              </span>
              <span
                className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary"
                data-testid="assessment-start-provenance"
              >
                {instrument.provenance}
              </span>
            </span>
            {instrument.groups.map((group) => (
              <div
                key={group.group_id}
                className="flex flex-col gap-8"
                data-testid={`assessment-start-group-${group.group_id}`}
              >
                <span className="font-sans text-s13 font-w600 tracking-givmo text-text-secondary">
                  {group.title}
                </span>
                <ul className="flex flex-col gap-10">
                  {group.statements.map((statement) => (
                    <li
                      key={statement.statement_id}
                      className="flex items-start justify-between gap-16"
                    >
                      <span className="flex min-w-0 flex-col gap-2">
                        <span className={STATEMENT}>{statement.text}</span>
                        {statement.source ? <SourceLine source={statement.source} /> : null}
                      </span>
                      <Switch
                        appearance="saas"
                        checked={!off.has(statement.statement_id)}
                        onCheckedChange={(on) => askStatement(statement.statement_id, on)}
                        aria-label={w.askStatement(statement.text)}
                        className="mt-2 shrink-0"
                        testId={`assessment-start-switch-${statement.statement_id}`}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            {(instrument.open_questions ?? []).length > 0 ? (
              <div className="flex flex-col gap-8" data-testid="assessment-start-group-open">
                {instrument.open_questions_title ? (
                  <span className="font-sans text-s13 font-w600 tracking-givmo text-text-secondary">
                    {instrument.open_questions_title}
                  </span>
                ) : null}
                <ul className="flex flex-col gap-10">
                  {(instrument.open_questions ?? []).map((question) => (
                    <li
                      key={question.question_id}
                      className="flex items-start justify-between gap-16"
                    >
                      <span className={STATEMENT}>{question.text}</span>
                      <Switch
                        appearance="saas"
                        checked={!off.has(question.question_id)}
                        onCheckedChange={(on) => askStatement(question.question_id, on)}
                        aria-label={w.askStatement(question.text)}
                        className="mt-2 shrink-0"
                        testId={`assessment-start-switch-${question.question_id}`}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {ratingsOn === 0 ? (
              <ErrorMessage testId="assessment-start-none-on">{w.keepOneStatement}</ErrorMessage>
            ) : null}
          </>
        )}
      </section>

      <section className="flex flex-col gap-8" data-testid="assessment-start-people">
        <PartLabel>{w.whosAsked}</PartLabel>
        {rosterQuery.isPending ? (
          <Skeleton className="h-48 w-full rounded-md" />
        ) : people.length === 0 ? (
          <p className={SECONDARY}>{w.nobodyOnRoster}</p>
        ) : (
          <>
            <p className={SECONDARY}>{w.whosAskedLine}</p>
            <ul className="grid grid-cols-1 gap-x-16 gap-y-10 sm:grid-cols-2">
              {people.map((seat) => (
                <li key={seat.id}>
                  <label className="flex cursor-pointer items-center gap-10">
                    <Checkbox
                      appearance="saas"
                      checked={!unticked.has(seat.id)}
                      onCheckedChange={(on) => askPerson(seat.id, on)}
                      testId={`assessment-start-person-${seat.id}`}
                    />
                    <span className="flex min-w-0 flex-col">
                      <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                        {seat.display_name}
                      </span>
                      <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
                        {capacityLine(seat)}
                      </span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
            {asked.length === 0 ? (
              <ErrorMessage testId="assessment-start-nobody">{w.askOnePerson}</ErrorMessage>
            ) : null}
            {notJoined.length > 0 ? <FieldNote>{w.notJoined(notJoined)}</FieldNote> : null}
            {noEmail.length > 0 ? <FieldNote>{w.noEmail(noEmail)}</FieldNote> : null}
          </>
        )}
      </section>

      {firstPerson !== null && dueOn.length > 0 ? (
        <section className="flex flex-col gap-8" data-testid="assessment-start-email">
          <PartLabel>{w.theEmail}</PartLabel>
          <EmailPreview
            testId="assessment-start-email-preview"
            organization={organization ?? w.emailYourOrganization}
            subject={w.emailSubject(label.trim())}
            button={w.emailButton}
            lines={assessmentEmailLines('request', {
              name: firstPerson.display_name,
              organization,
              label: label.trim(),
              dueOn,
              joined: seatConnection(firstPerson) === 'joined',
            })}
          />
        </section>
      ) : null}

      {error !== null ? <ErrorMessage testId="assessment-start-error">{error}</ErrorMessage> : null}
    </DialogFrame>
  );
}
